import {
  AssetLineStyle,
  InvestigationAssetType,
  LineProperties,
  PointEventType,
  PointProperties,
  TrackModel,
} from '@customer/store/apis/investigationsApi';
import { ReactElement } from 'react';
import * as L from 'leaflet';
import { GeoJSON, FeatureGroup, Pane, LayersControl } from 'react-leaflet';
import { Feature, LineString, Point } from 'geojson';
import {
  AssetControl,
  InvestigationEnitityIdProps,
  useInvestigationEntity,
} from '@customer/store/slices/investigationsSlice';
import { CustomPaneName } from '../mapConfig';
import { useTranslation } from 'react-i18next';

const ObjectTypeColors: Record<InvestigationAssetType, string> = {
  [InvestigationAssetType.Local]: 'black',
  [InvestigationAssetType.Remote]: '#50CCE4',
  [InvestigationAssetType.Nearby]: 'var(--static-grey-400)',
};

const PointEventTypeColors: Record<PointEventType, string> = {
  [PointEventType.Start]: 'var(--static-red-400)',
  [PointEventType.End]: '#72E06A',
  [PointEventType.Other]: 'var(--static-grey-400)',
};

const strokeWeight = 1;
const innerLineWeight = 4;
const outerLineWeight = innerLineWeight + 2 * strokeWeight;

// This pointToLayer function is only used for Local tracks.
const pointToCircleMarker = (
  feature: Feature<Point, PointProperties>,
  latlng: L.LatLng,
): L.Layer => {
  const color = PointEventTypeColors[feature.properties.pttyp];

  return L.circleMarker(latlng, {
    pane: CustomPaneName.TrackPoints, // Draw the point above all tracks.
    radius: 8,
    // Stroke styles
    stroke: true,
    color: ObjectTypeColors[InvestigationAssetType.Local],
    opacity: 1,
    weight: strokeWeight,
    // Fill styles
    fill: true,
    fillColor: color,
    fillOpacity: 1,
  });
};

const filterDuring = (feature: Feature<LineString, LineProperties>): boolean => {
  return feature.properties.st === AssetLineStyle.DuringInteraction;
};

const filterNotDuring = (feature: Feature<LineString, LineProperties>): boolean => {
  return feature.properties.st === AssetLineStyle.NotDuringInteraction;
};

// Filter out any irrelevant points.
const filterPoints = (feature: Feature<Point, PointProperties>): boolean => {
  return feature.properties.pttyp !== PointEventType.Other;
};

interface TracksGeojsonGroupProps extends InvestigationEnitityIdProps {
  tracks: TrackModel[];
  checked?: boolean;
}

export const TracksGeojsonGroup = ({
  siteId,
  investigationId,
  tracks = [],
  checked = true,
}: TracksGeojsonGroupProps): ReactElement => {
  const { t } = useTranslation();

  const { assetControls = {} } = useInvestigationEntity({
    siteId,
    investigationId,
  });

  return (
    <LayersControl.Overlay name={t('Tracks')} checked={checked}>
      <FeatureGroup key="geojson">
        {tracks.map(
          (
            { asset: assetId, line, points, typ: type }: TrackModel,
            idx: number,
          ): ReactElement | null => {
            // Find the control values for the current asset (if anything has been stored).
            const assetControl: AssetControl | undefined = assetControls[assetId];
            // Default to showing data if no control is found.
            const isVisible = assetControl?.visible ?? true;

            return (
              <FeatureGroup key={assetId}>
                <Pane
                  name={`${CustomPaneName.TrackRoutes}-child--${assetId}`}
                  style={{ zIndex: idx }} // This z-index exists in a new stack because this pane is nested within a parent pane.
                >
                  {line && isVisible && (
                    <>
                      {/* Continuous line behind the other line segments that gives the illusion of a black outline. */}
                      <GeoJSON data={line} style={{ weight: outerLineWeight, color: 'black' }} />

                      {type === InvestigationAssetType.Local ? (
                        <>
                          {/* Filter the lines based on whether the segment is during the event or not. */}
                          <GeoJSON
                            data={line}
                            style={{ weight: innerLineWeight, color: '#72E06A' }}
                            filter={filterNotDuring}
                          />
                          <GeoJSON
                            data={line}
                            style={{
                              weight: innerLineWeight,
                              color: 'var(--static-red-400)',
                              lineCap: 'butt',
                            }}
                            filter={filterDuring}
                          />
                        </>
                      ) : (
                        <GeoJSON
                          data={line}
                          style={{
                            weight: innerLineWeight,
                            color: ObjectTypeColors[type],
                          }}
                        />
                      )}
                    </>
                  )}
                </Pane>
                {/* Only show the points for Local type. */}
                {type === InvestigationAssetType.Local && points && isVisible && (
                  <GeoJSON data={points} pointToLayer={pointToCircleMarker} filter={filterPoints} />
                )}
              </FeatureGroup>
            );
          },
        )}
      </FeatureGroup>
    </LayersControl.Overlay>
  );
};

export default TracksGeojsonGroup;
